import { useState } from 'react';
import { createVendorSchema, VENDOR_TYPE_LABELS, VENDOR_TYPES, type Vendor, type VendorType } from '@faktorplus/shared';
import { useCreateVendor, useUpdateVendor, useVendors } from '../../api/dictionaries';
import { Button } from '../../components/ui/button';
import { PencilIcon } from '../../components/ui/icons';
import { Field, Input, Select } from '../../components/ui/form';
import { Modal, modalStyles } from '../../components/ui/modal';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import pageStyles from '../../components/layout/page.module.css';
import styles from './settings-page.module.css';

export function VendorsTab() {
  const { data: vendors = [], isLoading, error } = useVendors(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Vendor | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorBox>Nie udało się pobrać kontrahentów.</ErrorBox>;

  const term = search.trim().toLowerCase();
  const visible = term
    ? vendors.filter(
        (vendor) =>
          vendor.name.toLowerCase().includes(term) || (vendor.nip ?? '').includes(term),
      )
    : vendors;

  return (
    <>
      <div className={pageStyles.header}>
        <span className={pageStyles.subtitle}>{vendors.length} kontrahentów</span>
        <div className={pageStyles.actions}>
          <Button variant="primary" onClick={() => setIsCreating(true)}>
            + Nowy kontrahent
          </Button>
        </div>
      </div>

      <div className={pageStyles.filters}>
        <Input
          className={pageStyles.searchControl}
          placeholder="Szukaj po nazwie lub NIP…"
          aria-label="Szukaj kontrahenta"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {visible.length === 0 ? (
        <EmptyState mark="∅" title="Brak wyników" description="Zmień frazę wyszukiwania." />
      ) : (
        <div className={pageStyles.tableWrap}>
          <table className={pageStyles.table}>
            <thead>
              <tr>
                <th>Nazwa</th>
                <th>NIP</th>
                <th>Typ</th>
                <th>E-mail do zamówień</th>
                <th>Telefon</th>
                <th>Stan</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((vendor) => (
                <tr key={vendor.id} className={vendor.isActive ? undefined : styles.inactive}>
                  <td className={pageStyles.strong}>{vendor.name}</td>
                  <td className={pageStyles.docNumber}>{vendor.nip ?? '—'}</td>
                  <td>
                    <span
                      className={`${styles.chip} ${
                        vendor.type === 'SUBCONTRACTOR' ? styles.chipAccent : ''
                      }`}
                    >
                      {VENDOR_TYPE_LABELS[vendor.type]}
                    </span>
                  </td>
                  <td className={vendor.email ? undefined : pageStyles.muted}>
                    {vendor.email ?? '—'}
                  </td>
                  <td className={vendor.phone ? undefined : pageStyles.muted}>
                    {vendor.phone ?? '—'}
                  </td>
                  <td className={pageStyles.muted}>{vendor.isActive ? 'Aktywny' : 'Nieaktywny'}</td>
                  <td>
                    <div className={styles.rowActions}>
                      <Button variant="soft" icon={<PencilIcon />} onClick={() => setEditing(vendor)}>
                        Edytuj
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(isCreating || editing) && (
        <VendorDialog
          vendor={editing}
          onClose={() => {
            setIsCreating(false);
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

function VendorDialog({ vendor, onClose }: { vendor: Vendor | null; onClose: () => void }) {
  const create = useCreateVendor();
  const update = useUpdateVendor();

  const [name, setName] = useState(vendor?.name ?? '');
  const [nip, setNip] = useState(vendor?.nip ?? '');
  const [type, setType] = useState<VendorType>(vendor?.type ?? 'SUPPLIER');
  const [email, setEmail] = useState(vendor?.email ?? '');
  const [phone, setPhone] = useState(vendor?.phone ?? '');
  const [address, setAddress] = useState(vendor?.address ?? '');
  const [isActive, setIsActive] = useState(vendor?.isActive ?? true);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isPending = create.isPending || update.isPending;
  const serverError = create.error ?? update.error;

  async function handleSubmit() {
    setValidationError(null);

    const parsed = createVendorSchema.safeParse({
      name: name.trim(),
      nip: nip.trim() || null,
      type,
      email: email.trim() || null,
      phone: phone.trim() || null,
      address: address.trim() || null,
    });

    if (!parsed.success) {
      setValidationError(parsed.error.issues[0]?.message ?? 'Sprawdź wprowadzone dane');
      return;
    }

    if (vendor) {
      await update.mutateAsync({ id: vendor.id, input: { ...parsed.data, isActive } });
    } else {
      await create.mutateAsync(parsed.data);
    }
    onClose();
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={vendor ? 'Edytuj kontrahenta' : 'Nowy kontrahent'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Anuluj
          </Button>
          <span className={modalStyles.footerSpacer} />
          <Button variant="primary" isLoading={isPending} onClick={() => void handleSubmit()}>
            {vendor ? 'Zapisz zmiany' : 'Dodaj kontrahenta'}
          </Button>
        </>
      }
    >
      <Field label="Nazwa">
        {(id) => (
          <Input
            id={id}
            value={name}
            autoFocus={!vendor}
            placeholder="SIG"
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>

      <div className={modalStyles.row}>
        <Field label="NIP">
          {(id) => (
            <Input
              id={id}
              value={nip}
              placeholder="5270201470"
              onChange={(event) => setNip(event.target.value)}
            />
          )}
        </Field>

        <Field label="Typ">
          {(id) => (
            <Select
              id={id}
              value={type}
              onChange={(event) => setType(event.target.value as VendorType)}
            >
              {VENDOR_TYPES.map((candidate) => (
                <option key={candidate} value={candidate}>
                  {VENDOR_TYPE_LABELS[candidate]}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>

      <div className={modalStyles.row}>
        <Field label="E-mail do zamówień">
          {(id) => (
            <Input
              id={id}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>

        <Field label="Telefon">
          {(id) => (
            <Input id={id} value={phone} onChange={(event) => setPhone(event.target.value)} />
          )}
        </Field>
      </div>

      <Field label="Adres">
        {(id) => (
          <Input id={id} value={address} onChange={(event) => setAddress(event.target.value)} />
        )}
      </Field>

      {vendor && (
        <label className={modalStyles.checkItem}>
          <input
            type="checkbox"
            className={pageStyles.checkbox}
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
          />
          Kontrahent aktywny
        </label>
      )}

      {validationError && <ErrorBox>{validationError}</ErrorBox>}
      {serverError && (
        <ErrorBox>
          {serverError instanceof ApiError ? serverError.message : 'Nie udało się zapisać.'}
        </ErrorBox>
      )}
    </Modal>
  );
}
